/* eslint-disable  @typescript-eslint/no-explicit-any */
// tinymce's core module defines the `tinymce` global as a side effect. The plugin/theme/icon
// imports below dereference that global at module scope, so this import must run first. The
// bare 'tinymce' specifier resolves to the unminified tinymce.js, which esbuild fails to parse
// ("Unexpected var") in at least one consuming app's build - import the minified core instead,
// consistent with every other import in this file.
import 'tinymce/tinymce.min.js';
import 'tinymce/icons/default/icons.min.js';
import 'tinymce/themes/silver/theme.min.js';
import 'tinymce/plugins/image/plugin.min.js';
import 'tinymce/plugins/paste/plugin.min.js';
// autolink is bundled from npm because the GCS-hosted TinyMCE assets
// (tinymce.baseURL below) do not include it — without this import, listing
// it in `plugins` makes TinyMCE fetch it from the bucket and fail.
import 'tinymce/plugins/autolink/plugin.min.js';
// Note: autoresize is deliberately NOT imported here. Unlike autolink it is served from the bucket,
// and the copy there (5.0.0-1) was verified to behave identically to npm's 5.10 against this core —
// it touches no skin classes, only measured heights. Importing it statically also breaks the Jest
// suite, because the plugin dereferences the `tinymce` global at module scope and nothing defines it
// before the component's imports run. Leave it to be fetched from tinymce.baseURL like link/lists.
import {
  AfterViewChecked,
  booleanAttribute,
  Component,
  computed,
  ElementRef,
  forwardRef,
  HostBinding,
  inject,
  Input,
  OnDestroy,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Router } from '@angular/router';
import { BehaviorSubject, combineLatest, distinctUntilChanged, map, tap } from 'rxjs';
import { Editor } from 'tinymce';
import { isDarkTheme, StyleService } from '@vendasta/galaxy/style-service';
import { GalaxyTheme } from '@vendasta/galaxy/style-service/src/style.service';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';

declare let tinymce: any;

@Component({
  selector: 'glxy-rich-text-editor',
  templateUrl: './rich-text-editor.component.html',
  styleUrls: ['./rich-text-editor.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => RichTextEditorComponent),
    },
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class RichTextEditorComponent implements AfterViewChecked, OnDestroy, ControlValueAccessor {
  @Input() elementId = '';
  @Input({ transform: booleanAttribute }) disabled = false;
  @Input() menubar: any = false;
  @Input() statusbar = false;
  @Input() menu: any = false;
  /**
   * Toolbar contents. A string is a single toolbar and honours {@link toolbarMode}. An array puts
   * TinyMCE into multiple-toolbar mode, which renders one wrapping row per entry and ignores
   * `toolbarMode` for every mode except `scrolling` — so narrow viewports stack rows instead of
   * collapsing into the overflow menu. Prefer a string unless you genuinely want stacked rows.
   */
  @Input() toolbar: string | string[] | false = [
    'bold italic underline link unlink | alignleft aligncenter alignright alignjustify | bullist numlist | code removeformat',
  ];
  /**
   * How a toolbar too wide for the editor behaves: `floating` and `sliding` collapse the overflow
   * behind a `…` button, `scrolling` keeps one horizontally scrollable row, `wrap` stacks rows.
   * Only takes effect when {@link toolbar} is a string, except `scrolling` which also applies to
   * arrays.
   *
   * Defaults to `wrap` rather than TinyMCE's own `floating` because every mode except `wrap` needs
   * skin CSS this app does not currently load. `tinymce.baseURL` points at the GCS asset bucket,
   * whose light `oxide` skin is TinyMCE 5.0.0 and predates the split-toolbar DOM added in 5.4 — it
   * has no rules for `.tox-toolbar__primary`, `.tox-toolbar__overflow` or `.tox-toolbar--scrolling`.
   * Selecting `floating` there yields a toolbar collapsed to a lone `…` button that opens a
   * transparent, unpositioned drawer over the editor body. The bucket's custom `glxy-dark` skin *is*
   * current, so the failure is light-theme only. Do not change this default until the bucket's
   * `oxide` skin is rebuilt against the bundled TinyMCE version.
   */
  @Input() toolbarMode: 'floating' | 'sliding' | 'wrap' | 'scrolling' = 'wrap';
  @Input() toolbarPosition: 'auto' | 'top' | 'bottom' = 'top';
  @Input() plugins: string | false = 'lists link code';
  @Input() invalidElements = '';
  @Input({ transform: booleanAttribute }) allowResize = true;
  private enableThemes$$ = new BehaviorSubject<boolean>(false);
  @Input() set enableThemes(enable: boolean) {
    this.enableThemes$$.next(enable);
  }
  @Input() styleOverride = '';
  @Input() customSetup?: (editor: Editor) => void;
  @Input() browserSpellcheck = false;
  @Input() contentCss: string | string[] = '';
  @Input({ transform: booleanAttribute }) enableImageUpload = false;
  /**
   * Grow the editor to fit its content instead of filling its container. The editor body is an
   * iframe, so by default it is a second scroll container nested inside whatever scrolls the page —
   * a pointer or finger gesture over the body drives the iframe alone, and the surrounding form
   * cannot move. With `autoGrow` the iframe is always tall enough for its content and never scrolls,
   * leaving one scroll container: the host's.
   *
   * The host must also stop constraining the editor's height. Any `height: 100%` or `flex: 1` on
   * `glxy-rich-text-editor` or its ancestors will override the height this sets and silently undo
   * the effect. Pair with {@link minHeight} so an empty editor still has a usable target.
   */
  @Input({ transform: booleanAttribute }) autoGrow = false;
  /** Smallest height, in px, the editor may shrink to when {@link autoGrow} is set. */
  @Input() minHeight?: number;
  /** Largest height, in px, before {@link autoGrow} stops growing and the iframe scrolls again. */
  @Input() maxHeight?: number;

  @HostBinding('class.glxy-rte-auto-grow') get autoGrowClass(): boolean {
    return this.autoGrow;
  }

  isEditorReady = signal<boolean>(true);
  initialValue = ''; // used to store initial value, which is passed in before the editor can initialize

  private editor: Editor | undefined = undefined;
  private movedAuxSink: Element | undefined = undefined;
  private onChange: any;
  private onTouched: any;
  private styleService = inject(StyleService);
  private router = inject(Router);
  private elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
  private theme$ = combineLatest([this.styleService.activeTheme$, this.enableThemes$$]).pipe(
    takeUntilDestroyed(),
    map(([theme, enableThemes]) => {
      if (enableThemes) {
        return theme;
      }
      return GalaxyTheme.Light;
    }),
    distinctUntilChanged(),
    tap(() => {
      this.destroyEditor();
      this.isEditorReady.set(false);
    }),
  );
  theme = toSignal(this.theme$);
  isDark = computed(() => isDarkTheme(this.theme()));

  ngAfterViewChecked(): void {
    this.setupEditor();
  }

  ngOnDestroy(): void {
    this.destroyEditor();
  }

  onEditorChange(): void {
    if (!this.editor) {
      return;
    }
    const content = this.editor.getContent();
    if (this.onChange) {
      this.onChange(content);
      this.initialValue = content;
    }
  }

  writeValue(value: string): void {
    if (value == null) {
      return;
    }

    this.initialValue = value;

    if (this.editor) {
      this.editor.setContent(value);
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  insertContent(content: string): void {
    if (this.editor) {
      this.editor.insertContent(content);
    }
  }

  //TinyMCE editors need to be reloaded after dragged or moved. Use only if necessary.
  reloadEditor(): void {
    const currentContent = this.editor?.getContent() || this.initialValue;
    this.destroyEditor();
    this.isEditorReady.set(false);
    this.initialValue = currentContent;

    // Reinitialize the editor after a brief delay
    setTimeout(() => {
      this.setupEditor();
    }, 0);
  }

  setupEditor(): void {
    // in  order to edit parts of the CSS you'd need to edit the CSS files contained in the path below
    tinymce.baseURL = 'https://storage.googleapis.com/developers-vendasta-client/assets';
    const existingAuxSinks = new Set(document.querySelectorAll('body > .tox-tinymce-aux'));
    tinymce.init({
      skin: this.isDark() ? 'glxy-dark' : 'oxide',
      content_css: this.contentCss
        ? [
            this.isDark() ? 'glxy-dark' : 'default',
            ...(Array.isArray(this.contentCss) ? this.contentCss : [this.contentCss]),
          ]
        : this.isDark()
          ? 'glxy-dark'
          : 'default',
      content_style: this.styleOverride,
      browser_spellcheck: true,
      selector: '#' + this.elementId + '-' + this.theme(),
      readonly: this.disabled,
      width: '100%',
      height: this.autoGrow ? 'auto' : '100%',
      ...(this.autoGrow
        ? {
            ...(this.minHeight !== undefined ? { min_height: this.minHeight } : {}),
            ...(this.maxHeight !== undefined ? { max_height: this.maxHeight } : {}),
            autoresize_bottom_margin: 16,
          }
        : {}),
      menubar: this.menubar,
      statusbar: this.statusbar,
      menu: this.menu,
      toolbar: this.toolbar,
      toolbar_mode: this.toolbarMode,
      plugins: this.resolvePlugins(),
      contextmenu: '',
      convert_urls: false,
      allow_conditional_comments: true,
      valid_children: '+body[style]',
      invalid_elements: this.invalidElements || null,
      branding: false,
      // A drag-to-resize grabber fights an editor that sizes itself.
      resize: this.autoGrow ? false : this.allowResize,
      paste_data_images: this.enableImageUpload,
      toolbar_location: this.toolbarPosition,
      setup: (editor: Editor) => {
        this.editor = editor;

        // Call custom setup function if provided
        if (this.customSetup) {
          this.customSetup(editor);
        }

        editor.on('init', () => {
          if (this.initialValue) {
            this.editor?.setContent(this.initialValue);
          }
          const contentDoc = editor.getDoc();
          if (contentDoc) {
            contentDoc.addEventListener('click', (event: MouseEvent) => {
              const anchor = (event.target as HTMLElement).closest('a');
              if (!anchor) return;
              try {
                const url = new URL(anchor.href, window.location.origin);
                if (url.origin === window.location.origin) {
                  event.preventDefault();
                  this.router.navigateByUrl(url.pathname + url.search + url.hash);
                }
              } catch {
                // ignore malformed hrefs
              }
            });
          }
        });
        // prevents this.editor from being undefined on events
        const changeHandler = () => {
          this.onEditorChange();
        };
        editor.on('keyup', changeHandler);
        editor.on('change', changeHandler);
        editor.on('NodeChange', changeHandler);
      },
      init_instance_callback: () => {
        this.isEditorReady.set(true);
        this.moveAuxSinkIntoOverlayPane(existingAuxSinks);
      },
    });
  }

  // `autoGrow` is a Galaxy-level concept, so callers configure it with an input rather than by
  // remembering to add the plugin to `plugins` themselves.
  private resolvePlugins(): string | false {
    if (!this.autoGrow) {
      return this.plugins;
    }
    const plugins = this.plugins || '';
    return plugins.split(/\s+/).includes('autoresize') ? plugins : `${plugins} autoresize`.trim();
  }

  // TinyMCE appends its popup/dialog sink to <body>, where the CDK overlay marks it
  // aria-hidden whenever the editor is hosted inside a Material dialog, leaving editor
  // dialogs (e.g. insert link) unusable. Reparenting the sink into the overlay pane
  // keeps it visible and accessible.
  private moveAuxSinkIntoOverlayPane(existingAuxSinks: Set<Element>): void {
    const overlayPane = this.elementRef.nativeElement.closest('.cdk-overlay-pane');
    if (!overlayPane) {
      return;
    }
    const auxSink = Array.from(document.querySelectorAll('body > .tox-tinymce-aux')).find(
      (sink) => !existingAuxSinks.has(sink),
    );
    if (auxSink) {
      overlayPane.appendChild(auxSink);
      this.movedAuxSink = auxSink;
    }
  }

  private destroyEditor(): void {
    if (this.editor) {
      tinymce.remove(this.editor);
      this.editor.destroy();
      this.editor = undefined;
    }
    // We reparented the sink out of <body>, so remove it explicitly rather than
    // relying on TinyMCE to clean up a node whose location it no longer controls.
    this.movedAuxSink?.remove();
    this.movedAuxSink = undefined;
  }
}
